/**
 * ERROR HANDLING UTILITIES
 *
 * Chuẩn hóa cách hiển thị lỗi cho người dùng:
 * - Trích xuất message từ mọi loại error (tRPC, Axios, NestJS, Zod, generic)
 * - Hỗ trợ Zod field-level errors mapping
 * - Error code classification cho UI branching
 */

// ── Error Code Classification ──

export type ErrorCategory = 'validation' | 'auth' | 'permission' | 'not_found' | 'conflict' | 'network' | 'server' | 'unknown';

/**
 * Phân loại error code thành category cho UI xử lý phù hợp.
 * Ví dụ: 403 -> hiện modal "Liên hệ quản trị viên", 401 -> redirect login.
 */
export function classifyError(error: unknown): ErrorCategory {
  if (!error || typeof error !== 'object') return 'unknown';
  const err = error as Record<string, any>;

  // tRPC error shape
  const trpcCode = err.data?.code || err.shape?.data?.code;
  if (trpcCode) {
    switch (trpcCode) {
      case 'BAD_REQUEST':
      case 'UNPROCESSABLE_CONTENT':
        return 'validation';
      case 'UNAUTHORIZED':
        return 'auth';
      case 'FORBIDDEN':
        return 'permission';
      case 'NOT_FOUND':
        return 'not_found';
      case 'CONFLICT':
        return 'conflict';
      case 'TOO_MANY_REQUESTS':
      case 'INTERNAL_SERVER_ERROR':
        return 'server';
    }
  }

  // HTTP status
  const status = err.status || err.data?.httpStatus || err.response?.status;
  if (typeof status === 'number') {
    if (status === 401) return 'auth';
    if (status === 403) return 'permission';
    if (status === 404) return 'not_found';
    if (status === 409) return 'conflict';
    if (status >= 400 && status < 500) return 'validation';
    if (status >= 500) return 'server';
  }

  // Network errors
  if (err instanceof Error && (
    err.message === 'Failed to fetch' ||
    err.message?.includes('NetworkError') ||
    err.message?.includes('ERR_NETWORK')
  )) {
    return 'network';
  }

  return 'unknown';
}

// ── Error Title by Category ──

const ERROR_TITLES: Record<ErrorCategory, string> = {
  validation: 'Dữ liệu không hợp lệ',
  auth: 'Phiên đăng nhập hết hạn',
  permission: 'Không có quyền truy cập',
  not_found: 'Không tìm thấy dữ liệu',
  conflict: 'Xung đột dữ liệu',
  network: 'Lỗi kết nối mạng',
  server: 'Lỗi hệ thống',
  unknown: 'Có lỗi xảy ra',
};

/** Lấy tiêu đề lỗi tiếng Việt theo category */
export function getErrorTitle(error: unknown): string {
  return ERROR_TITLES[classifyError(error)];
}

// ── Error Message Extraction ──

/**
 * Trích xuất message lỗi readable từ bất kỳ error object nào.
 * Xử lý: tRPC, Axios, NestJS HttpException, Zod, plain Error, string.
 */
export function extractErrorMessage(error: unknown, fallback = 'Có lỗi xảy ra trong quá trình xử lý'): string {
  if (!error) return fallback;
  if (typeof error === 'string') return error;

  // Check if error contains tRPC zodError flatten structure first
  if (typeof error === 'object') {
    const errObj = error as Record<string, any>;
    const zodError = errObj.data?.zodError || errObj.shape?.data?.zodError || errObj.zodError;
    if (zodError) {
      const fieldErrors = extractZodFieldErrors(error);
      if (fieldErrors.length > 0) {
        return fieldErrors.map((fe) => (fe.field && fe.field !== '_form' ? `${fe.field}: ${fe.message}` : fe.message)).join('; ');
      }
    }
  }

  const formatRawMessage = (msg: string): string => {
    if (msg === 'Failed to fetch') {
      return 'Không thể kết nối tải tệp lên máy chủ hoặc tệp bị chặn bởi kết nối mạng. Vui lòng thử lại hoặc giảm kích thước tệp.';
    }
    const trimmed = msg.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.message) {
          return parsed
            .map((issue: any) => {
              const pathStr = Array.isArray(issue.path) && issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
              return `${pathStr}${issue.message}`;
            })
            .join('; ');
        }
      } catch {
        /* ignore JSON parse error */
      }
    }
    return msg;
  };

  if (error instanceof Error) {
    return formatRawMessage(error.message);
  }

  if (typeof error === 'object') {
    const errObj = error as Record<string, any>;

    // Axios response
    if (errObj.response?.data?.message && typeof errObj.response.data.message === 'string') {
      return formatRawMessage(errObj.response.data.message);
    }

    // Standard message field
    if (errObj.message && typeof errObj.message === 'string') {
      return formatRawMessage(errObj.message);
    }

    // Error field
    if (errObj.error && typeof errObj.error === 'string') {
      return formatRawMessage(errObj.error);
    }

    try {
      const str = JSON.stringify(error);
      if (str && str !== '{}') return str;
    } catch {
      /* ignore */
    }
  }

  return fallback;
}

// ── Zod Error Extraction ──

export interface FieldError {
  field: string;
  message: string;
}

/**
 * Trích xuất Zod validation errors thành field-level errors.
 * Dùng để map lỗi server về form fields trên frontend.
 */
export function extractZodFieldErrors(error: unknown): FieldError[] {
  if (!error || typeof error !== 'object') return [];
  const errObj = error as Record<string, any>;

  // tRPC trả về zodError trong data (khi được bật)
  const zodError = errObj.data?.zodError || errObj.zodError;
  if (!zodError) return [];

  const fieldErrors: FieldError[] = [];

  // fieldErrors format: { fieldName: ["message1", "message2"] }
  if (zodError.fieldErrors && typeof zodError.fieldErrors === 'object') {
    for (const [field, messages] of Object.entries(zodError.fieldErrors)) {
      if (Array.isArray(messages)) {
        for (const msg of messages) {
          if (typeof msg === 'string') {
            fieldErrors.push({ field, message: msg });
          }
        }
      }
    }
  }

  // formErrors format: ["message1", "message2"]
  if (Array.isArray(zodError.formErrors)) {
    for (const msg of zodError.formErrors) {
      if (typeof msg === 'string') {
        fieldErrors.push({ field: '_form', message: msg });
      }
    }
  }

  return fieldErrors;
}

/**
 * Kiểm tra error có phải Zod validation error không.
 */
export function hasZodErrors(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const errObj = error as Record<string, any>;
  return !!(errObj.data?.zodError || errObj.zodError);
}
